/**
 * <newsletter-form>
 *
 * Drives the form in sections/section-newsletter.liquid: submits Shopify's
 * customer form over fetch so the page never reloads, then shows a message and
 * clears the fields.
 *
 * PROGRESSIVE ENHANCEMENT, NOT A REQUIREMENT. The markup is a real
 * `{% form 'customer' %}` that posts and works with scripting disabled — Shopify
 * re-renders the page with `form.posted_successfully?` and the section prints the
 * message server-side. Everything here only removes the reload.
 *
 * ON SUCCESS IT DISPATCHES a bubbling `newsletter:subscribed` event. That is the
 * whole integration surface for anything wrapping this element — the popup uses
 * it rather than reimplementing the submit.
 *
 * IT NEVER REPORTS A FAILURE. It can prove that Shopify accepted the signup; it
 * cannot prove the opposite, because "the response was unreadable" and "the signup
 * was rejected" are indistinguishable from here. So a confirmed success is handled
 * in place, and EVERYTHING ELSE is handed back to the browser as an ordinary form
 * post — the page reloads and Shopify renders the real outcome server-side.
 *
 * That is why this file contains no error message. Earlier versions guessed, and
 * told customers "Something went wrong" while their contact was being created
 * perfectly well. Shopify publishes no AJAX contract for `{% form 'customer' %}`
 * to guess against: the docs cover only that it posts to /contact#contact_form.
 *
 * Success is confirmed WITHOUT following the redirect. Shopify answers an accepted
 * signup with a 302 to the store's PRIMARY domain whatever host served the form,
 * so on the dev preview or the .myshopify.com host that redirect is cross-origin
 * and following it makes fetch reject on a signup that worked. `redirect: 'manual'`
 * turns that redirect into the success signal instead of a hazard.
 *
 * An address that is already subscribed still comes back as a success. That is
 * deliberate: telling a customer they are already on the list is worse than
 * thanking them twice, and it leaks who has signed up.
 */
if (!customElements.get('newsletter-form')) {
  customElements.define(
    'newsletter-form',
    class NewsletterForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.message = this.querySelector('[data-newsletter-message]');
        this.fields = Array.from(this.querySelectorAll('[data-newsletter-field]'));
        this.button = this.querySelector('[type="submit"]');

        if (!this.form || !this.message) {
          return;
        }

        this.isSubmitting = false;

        this.handleSubmit = this.handleSubmit.bind(this);
        this.form.addEventListener('submit', this.handleSubmit);
      }

      disconnectedCallback() {
        if (this.form) {
          this.form.removeEventListener('submit', this.handleSubmit);
        }
      }

      async handleSubmit(event) {
        /* Guards a double press. The button is disabled below too, but a keyboard
           repeat can fire submit again before the disable has painted. */
        if (this.isSubmitting) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        this.setSubmitting(true);

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
            redirect: 'manual',
          });

          if (await this.isConfirmedSuccess(response)) {
            this.handleSuccess();
            this.setSubmitting(false);
            return;
          }
        } catch {
          /* Swallowed on purpose. Whatever went wrong, the browser can do this
             properly — see below. */
        }

        this.submitNatively();
      }

      /* THIS COMPONENT NEVER SHOWS AN ERROR OF ITS OWN, AND THAT IS DELIBERATE.

         It can only prove one thing: that Shopify accepted the signup. It cannot
         prove the opposite. "I could not read the response" and "the signup was
         rejected" look identical from here, and reporting the first as the second
         is exactly the bug that plagued this section — customers were told
         "Something went wrong" while their contact was being created correctly.

         Shopify publishes no AJAX contract for `{% form 'customer' %}`. The docs
         cover only that it posts to /contact#contact_form: no documented success
         response, no guaranteed redirect target, no status code to rely on. So any
         negative verdict reached here is guesswork dressed up as a fact.

         Instead, anything short of a CONFIRMED success is handed back to the
         browser as an ordinary form post. The page reloads, Shopify renders the
         real outcome through `form.posted_successfully?` / `form.errors`, and the
         customer sees the truth from the only party that knows it. A false error
         becomes impossible rather than unlikely.

         The cost is one duplicate POST on that path. It is harmless here: the same
         address posted twice is simply already subscribed, which Shopify still
         answers as success. Correct feedback is worth more than a saved request. */
      submitNatively() {
        /* form.submit() does not fire a submit event, so this cannot re-enter the
           handler. The button stays disabled — the navigation is already starting
           and re-enabling it would only invite a second press. */
        this.isSubmitting = false;
        this.form.submit();
      }

      /* True ONLY when Shopify is known to have accepted the signup. Every other
         outcome — including "cannot tell" — returns false, which routes to a
         native submit rather than to an error message. See submitNatively above.

         Never follows the redirect. Shopify answers a successful signup with a 302
         to the store's PRIMARY domain whatever host served the form, so on the dev
         preview or the .myshopify.com host that redirect is cross-origin; following
         it makes fetch reject on a signup that actually worked. */
      async isConfirmedSuccess(response) {
        /* Shopify redirected, which is what it does when it accepts the signup.
           With redirect:'manual' the response is opaque — no status, no body — and
           that opacity is itself the signal. Status 0 is checked too because the
           two travel together and engines have differed on which they expose. */
        if (response.type === 'opaqueredirect' || response.status === 0) {
          return true;
        }

        if (!response.ok) {
          return false;
        }

        let html = '';

        try {
          html = await response.text();
        } catch {
          return false;
        }

        if (!html) {
          return false;
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        /* Scoped to this form's id first. The newsletter popup renders a second
           <newsletter-form> on every page from the header group, so an unscoped
           lookup could read the other one's result. */
        const scoped = this.form.id
          ? doc.querySelector(`#${CSS.escape(this.form.id)} [data-newsletter-message]`)
          : null;
        const node = scoped || doc.querySelector('[data-newsletter-message]');

        return Boolean(node) && node.dataset.newsletterState === 'success';
      }

      handleSuccess() {
        this.setMessage(this.dataset.messageSuccess);

        /* Bubbles so an ancestor can react without this file knowing who is
           listening. sections/section-newsletter-popup.liquid wraps one of these
           and closes itself on it; the standalone newsletter section has no
           listener and is unaffected. */
        this.dispatchEvent(new CustomEvent('newsletter:subscribed', { bubbles: true }));

        this.fields.forEach((field) => {
          field.value = '';
        });

        /* Focus returns to the first field rather than to the message. The
           message is a polite live region and announces itself; moving focus
           onto it would strand a keyboard user on a paragraph they cannot leave
           by tabbing backwards. */
        if (this.fields.length) {
          this.fields[0].focus();
        }
      }

      setSubmitting(state) {
        this.isSubmitting = state;
        this.form.setAttribute('aria-busy', state);

        if (this.button) {
          this.button.disabled = state;
        }
      }

      setMessage(text) {
        this.message.textContent = text || '';
      }
    },
  );
}
